import { useState } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useContract } from '../../hooks/useQueries'
import { Can } from '../../contexts/PermissionsContext'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { ContractClausesTab } from '../ContractClausesTab'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { AssemblySummary } from './AssemblySummary'
import { AttendanceTab } from './AttendanceTab'
import { ConvocationTab } from './ConvocationTab'
import { ResolutionsTab } from './ResolutionsTab'
import { SittingTab } from './SittingTab'
import { apiErrorMessage, useAssemblyAttendance, useAssemblyMutations } from './assembly-api'

type Tab = 'resolutions' | 'attendance' | 'convocation' | 'sitting' | 'details'
const TABS: Tab[] = ['attendance', 'convocation', 'sitting', 'details']

/**
 * One assembly: its minutes (PV) with the resolutions, the attendance sheet and
 * the minutes' own clauses. The selected tab and resolution live in the URL.
 * Spec 2026-10-05-01 §8.
 */
export function AssemblyPage() {
    const { org, uid } = useParams({ from: '/$org/assemblies/$uid' })
    const search = useSearch({ from: '/$org/assemblies/$uid' })
    const navigate = useNavigate()
    const { t } = useTranslation('assemblies')
    const { data: contract, isLoading } = useContract(org, uid, ['contract_type', 'assets'])
    const { approve, recordSigned } = useAssemblyMutations(org)
    const { data: attendance } = useAssemblyAttendance(org, uid)
    const [signing, setSigning] = useState(false)
    const [signedAt, setSignedAt] = useState('')
    const signError = apiErrorMessage(recordSigned.error)

    const minutes = contract as
        | {
              uid: string
              name?: string
              code?: string
              status?: string
              flags?: string[]
              contract_type?: { uid?: string; code?: string }
              contract_type_uid?: string
          }
        | undefined
    const name = minutes?.name || minutes?.code || '…'
    useBreadcrumbs([{ title: t('title'), path: `/${org}/assemblies` }, { title: name }])

    const tab: Tab = TABS.includes(search.tab as Tab) ? (search.tab as Tab) : 'resolutions'
    const setTab = (next: string) =>
        navigate({
            to: '/$org/assemblies/$uid',
            params: { org, uid },
            search: { tab: TABS.includes(next as Tab) ? (next as Exclude<Tab, 'resolutions'>) : undefined, resolution: undefined },
            replace: true,
        })

    if (isLoading) return <p className="text-sm text-muted-foreground">{t('loading')}</p>
    if (!minutes || minutes.contract_type?.code !== 'ASSEMBLY_MINUTES') {
        return (
            <div className="space-y-2" data-testid="assembly-not-found">
                <p>{t('not_found')}</p>
                <Link to="/$org/assemblies" params={{ org }} search={{ asset: undefined }} className="text-sm underline">
                    {t('title')}
                </Link>
            </div>
        )
    }
    const closed = minutes.status === 'closed'
    const mode = (attendance as { mode?: string } | undefined)?.mode ?? ''
    const held = (attendance as { held?: boolean } | undefined)?.held ?? false
    const convened = minutes.status !== 'draft' && mode !== 'recorded'
    // The vote and the outcome are typed only on a held meeting that the sitting does not run.
    const entryLocked = !closed && (!held || (mode === 'platform' && minutes.status !== 'closed'))
    const canSign = !closed && mode !== 'platform'
    const toApprove = minutes.flags?.includes('to_approve')

    return (
        <div className="space-y-6" data-testid="assembly-page">
            <PageHeader
                icon={SECTION_ICONS.assemblies}
                title={name}
                titleAside={
                    <>
                        {toApprove && <Badge variant="warning" data-testid="assembly-to-approve">{t('to_approve')}</Badge>}
                        <Badge variant="outline" data-testid="assembly-status-badge">{t(`status.${minutes.status ?? 'draft'}`)}</Badge>
                    </>
                }
                actions={
                    <Can perm="contracts.write">
                        {toApprove && (
                            <Button
                                onClick={() => approve.mutate({ params: { path: { orgCode: org, contractUid: uid } } })}
                                disabled={approve.isPending}
                                data-testid="assembly-approve"
                            >
                                {t('approve')}
                            </Button>
                        )}
                        {canSign && (
                            <Button variant="outline" onClick={() => setSigning((v) => !v)} data-testid="assembly-record-signed">
                                {t('record_signed.action')}
                            </Button>
                        )}
                    </Can>
                }
            />

            {signing && canSign && (
                <div className="flex flex-wrap items-end gap-2 rounded-md border p-3" data-testid="record-signed-form">
                    <label className="text-sm">
                        {t('record_signed.date')}
                        <Input type="date" value={signedAt} onChange={(e) => setSignedAt(e.target.value)} data-testid="record-signed-date" />
                    </label>
                    <Button
                        size="sm"
                        disabled={!signedAt || recordSigned.isPending}
                        data-testid="record-signed-confirm"
                        onClick={() =>
                            recordSigned.mutate(
                                { params: { path: { orgCode: org, contractUid: uid } }, body: { signed_at: signedAt } },
                                { onSuccess: () => setSigning(false) },
                            )
                        }
                    >
                        {t('record_signed.confirm')}
                    </Button>
                    {signError && (
                        <p className="text-sm text-destructive" data-testid="record-signed-error">{signError}</p>
                    )}
                </div>
            )}

            <AssemblySummary org={org} uid={uid} status={minutes.status ?? 'draft'} />

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="assembly-tabs">
                    <TabsTrigger value="resolutions" data-testid="assembly-tab-resolutions">{t('tabs.resolutions')}</TabsTrigger>
                    <TabsTrigger value="attendance" data-testid="assembly-tab-attendance">{t('tabs.attendance')}</TabsTrigger>
                    <TabsTrigger value="convocation" data-testid="assembly-tab-convocation">{t('tabs.convocation')}</TabsTrigger>
                    <TabsTrigger value="sitting" data-testid="assembly-tab-sitting">{t('tabs.sitting')}</TabsTrigger>
                    <TabsTrigger value="details" data-testid="assembly-tab-details">{t('tabs.details')}</TabsTrigger>
                </TabsList>
                <TabsContent value="resolutions">
                    <ResolutionsTab
                        org={org}
                        minutesUid={uid}
                        closed={closed}
                        entryLocked={entryLocked}
                        convened={convened}
                        held={held}
                        selected={search.resolution}
                        q={search.q}
                        outcome={search.outcome}
                    />
                </TabsContent>
                <TabsContent value="attendance">
                    <AttendanceTab org={org} minutesUid={uid} status={minutes.status ?? 'draft'} />
                </TabsContent>
                <TabsContent value="convocation">
                    <ConvocationTab org={org} minutesUid={uid} />
                </TabsContent>
                <TabsContent value="sitting">
                    <SittingTab org={org} minutesUid={uid} status={minutes.status ?? 'draft'} />
                </TabsContent>
                <TabsContent value="details">
                    <ContractClausesTab
                        org={org}
                        contractUid={uid}
                        contractTypeUid={minutes.contract_type?.uid || minutes.contract_type_uid || ''}
                        readOnly={closed}
                    />
                </TabsContent>
            </Tabs>
        </div>
    )
}
